import React, { useEffect, useState } from 'react';
import { Box, Skeleton, IconButton, Tooltip } from '@mui/material';
import { 
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  BrokenImage as BrokenImageIcon
} from '@mui/icons-material';
import { getSignedFileUrl } from '../../services/api';
import { getFileUrl, isImageFile, selectBestVersion } from '../../utils/fileHelpers';

/**
 * Smart image viewer that automatically uses appropriate versions
 * 
 * @param {Object} props
 * @param {Object|string} props.file - File object or URL
 * @param {string} props.displayType - Display context (thumbnail, preview, full)
 * @param {boolean} props.showDownload - Show download button
 * @param {Function} props.onDownload - Custom download handler
 * @param {boolean} props.fallbackToOriginal - Fallback to original if version unavailable
 * @param {Object} props.sx - Additional styles
 * @param {string} props.alt - Alt text
 */
const ImageViewer = ({
  file,
  displayType = 'preview',
  showDownload = false,
  onDownload,
  fallbackToOriginal = true,
  sx = {},
  alt = 'Image',
  ...props
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      if (!file) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        // Handle local/blob URLs
        if (typeof file === 'string' && (file.startsWith('blob:') || file.startsWith('data:'))) {
          setImageUrl(file);
          setLoading(false);
          return;
        }

        // Select best version for display type
        const selectedVersion = selectBestVersion(file, displayType);
        
        if (selectedVersion && selectedVersion.key) {
          // Get signed URL for the selected version
          // Only pass version parameter if it's explicitly set (not null)
          const options = selectedVersion.version ? { version: selectedVersion.version } : {};
          const signedUrl = await getSignedFileUrl(selectedVersion.key, options);
          setImageUrl(signedUrl);
        } else if (typeof file === 'string') {
          // Handle string URLs
          const signedUrl = await getSignedFileUrl(file);
          setImageUrl(signedUrl);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        setError(true);
        
        // Try fallback to original if enabled
        if (fallbackToOriginal && file.key) {
          try {
            const fallbackUrl = await getSignedFileUrl(file.key);
            setImageUrl(fallbackUrl);
            setError(false);
          } catch (fallbackErr) {
            console.error('Fallback also failed:', fallbackErr);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [file, displayType, fallbackToOriginal]);

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(file);
      return;
    }

    // Default download behavior
    try {
      // Always download original version
      const originalKey = typeof file === 'object' ? file.key : file;
      const downloadUrl = await getSignedFileUrl(originalKey);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.originalname || file.fileName || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Skeleton 
        variant="rectangular" 
        sx={{ width: '100%', height: '100%', ...sx }}
        animation="wave"
      />
    );
  }

  // Error state
  if (error || !imageUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          color: 'grey.500',
          width: '100%',
          height: '100%',
          ...sx
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 48 }} />
      </Box>
    );
  }

  // Check if file is actually an image
  if (!isImageFile(file) && typeof file === 'object') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          color: 'grey.700',
          width: '100%',
          height: '100%',
          p: 2,
          ...sx
        }}
      >
        Non-image file
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', ...sx }}>
      <img
        src={imageUrl}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          ...props.style
        }}
        onError={() => setError(true)}
        {...props}
      />
      
      {showDownload && !error && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
            opacity: 0,
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 1 },
            ...(sx['& .actions'] || {})
          }}
          className="actions"
        >
          <Tooltip title="Download original">
            <IconButton
              size="small"
              onClick={handleDownload}
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default ImageViewer;